import { useEffect } from 'react'

/**
 * Breadcrumbs - injects a visible breadcrumb trail into the active page.
 *
 * The SPA mounts every route's <main class="page"> at once and the inline
 * router in Pentabay.html toggles `.page.active` + stamps
 * <html data-route="…"> on each navigation. This component watches that
 * data-route attribute (plus popstate / hashchange / pb-pages-mounted) and,
 * on every route change, inserts (or refreshes) a <nav class="pb-breadcrumbs">
 * as the first child of the active page - directly below the sticky header,
 * above the hero.
 *
 * The trail (Home → [Services|Industries|Blog hub] → current page) mirrors
 * the BreadcrumbList JSON-LD emitted by RouteSEO.jsx, so the visible and
 * structured-data breadcrumbs stay in sync. The home route gets no
 * breadcrumb (a single "Home" crumb adds no value). Links are plain
 * internal hrefs, so the router's global click handler intercepts them for
 * client-side navigation.
 */

// Human labels per route (mirrors CRUMB_NAME in RouteSEO.jsx). Falls back to
// a title-cased slug for any route not listed here.
const CRUMB_NAME = {
  '/services': 'Services',
  '/custom-software': 'Custom Software',
  '/ai-data': 'AI & Data',
  '/cloud-devops': 'Cloud & DevOps',
  '/cloud-service': 'Cloud Services',
  '/design': 'Design',
  '/mobile-development': 'Mobile Development',
  '/saas-platforms': 'SaaS Platforms',
  '/digital-marketing': 'Digital Marketing',
  '/seo-services': 'SEO Services',
  '/ppc-google-ads': 'PPC / Google Ads',
  '/cybersecurity': 'Cybersecurity',
  '/cybersecurity-assessment': 'Cybersecurity Assessment',
  '/managed-cybersecurity': 'Managed Cybersecurity',
  '/penetration-testing': 'Penetration Testing',
  '/low-code-platforms': 'Low-code / No-code',
  '/industries': 'Industries',
  '/healthcare': 'Healthcare',
  '/fintech': 'Fintech',
  '/public-sector': 'Public Sector',
  '/banking-lending': 'Banking & Lending',
  '/telecommunications': 'Telecommunications',
  '/ai-edtech': 'AI & EdTech',
  '/saas-b2b': 'SaaS & B2B',
  '/real-estate': 'Real Estate',
  '/enterprise-it': 'Enterprise IT',
  '/trust': 'Trust',
  '/compliance': 'Compliance',
  '/insights': 'Newsroom',
  '/about': 'About',
  '/careers': 'Careers',
  '/contact-us': 'Contact',
  '/privacy-policy': 'Privacy Policy',
  '/cookies-policy': 'Cookies Policy',
  '/our-clients': 'Our Clients',
  '/our-work': 'Our Work',
  '/software-development-toronto': 'Toronto & the GTA',
  '/software-development-canada': 'Canada',
  '/software-development-usa': 'United States',
  '/phipa-vs-hipaa-canadian-healthcare-software-compliance': 'PHIPA vs. HIPAA',
  '/ai-data-compliant-ai-canada': 'Compliant AI in Canada',
  '/product-design-compliance-decision-aoda': 'Product Design & Compliance',
  '/blog': 'Blog',
  '/healthcare-software-development-guide': 'Healthcare Software Guide',
  '/fhir-hl7-integration-guide': 'FHIR & HL7 Integration Guide',
  '/custom-software-development-cost-canada': 'Custom Software Cost (Canada)',
  '/soc-2-ready-vendor-guide': 'SOC 2-Ready Vendors',
  '/public-sector-software-buyers': 'Public Sector Buyers',
  '/low-code-no-code-when-it-actually-works': 'Low-Code vs No-Code',
  '/mobile-development-native-vs-cross-platform': 'Native vs Cross-Platform',
  '/web-marketing-sites-what-actually-matters': 'Marketing Website Builds',
  '/cloud-devops-what-it-actually-includes': 'What Cloud & DevOps Includes',
  '/enterprise-it-legacy-modernization': 'Legacy System Modernization',
  '/ai-edtech-student-data-privacy-ferpa-coppa': 'AI in EdTech: FERPA & COPPA',
  '/real-estate-proptech-what-software-actually-needs-to-do': 'PropTech Software',
  '/b2b-saas-what-enterprise-buyers-actually-check': 'Enterprise Buyer Checks',
  '/banking-lending-software-core-system-integration': 'Banking & Lending Software',
  '/how-to-choose-a-software-development-company': 'Choosing a Dev Company',
  '/custom-software-vs-off-the-shelf': 'Custom vs Off-the-Shelf',
  '/offshore-vs-nearshore-software-development': 'Offshore vs Nearshore',
  '/healthcare-software-vendor-checklist': 'Vendor Checklist',
  '/prior-authorization-automation-what-actually-changes': 'Prior Authorization Automation',
  '/remote-patient-monitoring-what-software-needs-to-handle': 'Remote Patient Monitoring',
  '/ai-symptom-checkers-triage-what-the-line-actually-is': 'AI Symptom Checkers',
  '/digital-front-door-healthcare-what-it-actually-requires': 'Digital Front Door',
  '/population-health-management-what-actually-matters': 'Population Health',
}

/* Service pages that sit UNDER another service page rather than directly
   under the /services hub. Added 2026-09-21 with the Digital Marketing and
   Cybersecurity families.

   The site's URLs are flat - the router in Pentabay.html only matches a
   single path segment, so /digital-marketing/seo-services cannot exist as a
   URL. The hierarchy is therefore carried entirely by this map, which turns
   the trail from three levels into four:

     Home > Services > Digital Marketing > SEO Services

   Keep in sync with the identical map in the other file (this constant is
   duplicated in RouteSEO.jsx and Breadcrumbs.jsx for the same reason
   CRUMB_NAME and SERVICE_ROUTES already are - the two modules must not
   import from each other, because generate-seo-html.mjs parses RouteSEO.jsx
   as a standalone text file at build time and cannot resolve imports). */
const SERVICE_PARENT = {
  '/seo-services': '/digital-marketing',
  '/ppc-google-ads': '/digital-marketing',
  '/cybersecurity-assessment': '/cybersecurity',
  '/managed-cybersecurity': '/cybersecurity',
  '/penetration-testing': '/cybersecurity',
}

const SERVICE_ROUTES = new Set([
  '/custom-software', '/ai-data', '/cloud-devops', '/cloud-service', '/design',
  '/mobile-development', '/saas-platforms', '/digital-marketing', '/low-code-platforms',
  '/seo-services', '/ppc-google-ads',
  '/cybersecurity', '/cybersecurity-assessment', '/managed-cybersecurity',
  '/penetration-testing',
])
const INDUSTRY_ROUTES = new Set([
  '/healthcare', '/fintech', '/public-sector', '/banking-lending',
  '/telecommunications', '/ai-edtech', '/saas-b2b', '/real-estate', '/enterprise-it',
])
const BLOG_ROUTES = new Set([
  '/healthcare-software-development-guide', '/fhir-hl7-integration-guide',
  '/custom-software-development-cost-canada', '/soc-2-ready-vendor-guide',
  '/how-to-choose-a-software-development-company',
  '/custom-software-vs-off-the-shelf', '/offshore-vs-nearshore-software-development',
  '/healthcare-software-vendor-checklist', '/ai-data-compliant-ai-canada',
  '/product-design-compliance-decision-aoda', '/public-sector-software-buyers',
  '/cloud-devops-what-it-actually-includes',
  '/low-code-no-code-when-it-actually-works',
  '/enterprise-it-legacy-modernization',
  '/ai-edtech-student-data-privacy-ferpa-coppa',
  '/real-estate-proptech-what-software-actually-needs-to-do',
  '/mobile-development-native-vs-cross-platform',
  '/web-marketing-sites-what-actually-matters',
  '/b2b-saas-what-enterprise-buyers-actually-check',
  '/banking-lending-software-core-system-integration',
  '/telecommunications-software-what-actually-breaks',
  '/agentic-ai-regulated-industries-what-actually-changes',
  '/shadow-ai-compliance-risk',
  '/prior-authorization-automation-what-actually-changes',
  '/remote-patient-monitoring-what-software-needs-to-handle',
  '/ai-symptom-checkers-triage-what-the-line-actually-is',
  '/digital-front-door-healthcare-what-it-actually-requires',
  '/population-health-management-what-actually-matters',
])

function crumbName(route) {
  if (route === '/') return 'Home'
  if (CRUMB_NAME[route]) return CRUMB_NAME[route]
  return route.replace(/^\//, '').split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

// Resolve the active route: clean /path first, legacy hash fragment as a
// fallback (matches RouteSEO.currentRoute()).
function currentRoute() {
  const path = window.location.pathname || '/'
  const m = path.match(/^\/([a-z0-9-]+)\/?$/i)
  if (m && m[1]) return '/' + m[1]
  const raw = window.location.hash || ''
  if (!raw || raw === '#' || raw === '#/') return '/'
  let hp = raw.startsWith('#') ? raw.slice(1) : raw
  if (!hp.startsWith('/')) hp = '/' + hp
  const idx = hp.indexOf('#')
  if (idx !== -1) hp = hp.slice(0, idx)
  return hp || '/'
}

function trailFor(route) {
  const trail = [{ name: 'Home', path: '/' }]
  if (SERVICE_ROUTES.has(route)) {
    trail.push({ name: 'Services', path: '/services' })
    /* A child service gets its parent as a fourth level, which is the only
       place the Digital Marketing / Cybersecurity hierarchy is expressed -
       the URLs are flat. Guarded against a parent that is missing from
       SERVICE_ROUTES so a typo degrades to the old three-level trail rather
       than emitting a crumb that links nowhere. */
    const parent = SERVICE_PARENT[route]
    if (parent && parent !== route) trail.push({ name: crumbName(parent), path: parent })
  }
  else if (INDUSTRY_ROUTES.has(route)) trail.push({ name: 'Industries', path: '/industries' })
  else if (BLOG_ROUTES.has(route)) trail.push({ name: 'Blog', path: '/blog' })
  trail.push({ name: crumbName(route), path: route })
  return trail
}

function esc(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function render() {
  const route = currentRoute()
  const routeName = route === '/' ? 'home' : route.slice(1)
  const active = document.querySelector('.page.active')
    || document.getElementById('page-' + routeName)
  if (!active) return

  let nav = active.querySelector(':scope > nav.pb-breadcrumbs')

  // No breadcrumb on the home route - remove any stale one.
  if (route === '/') { if (nav) nav.remove(); return }

  const trail = trailFor(route)
  const items = trail.map((c, i) => {
    if (i === trail.length - 1) {
      return `<li><span class="pb-bc-cur" aria-current="page">${esc(c.name)}</span></li>`
    }
    return `<li><a href="${c.path}">${esc(c.name)}</a>`
      + `<span class="pb-bc-sep" aria-hidden="true">/</span></li>`
  }).join('')
  const html = `<ol class="pb-bc">${items}</ol>`

  if (!nav) {
    nav = document.createElement('nav')
    nav.className = 'pb-breadcrumbs shell'
    nav.setAttribute('aria-label', 'Breadcrumb')
    active.insertBefore(nav, active.firstChild)
  }
  if (nav.innerHTML !== html) nav.innerHTML = html
}

export default function Breadcrumbs() {
  useEffect(() => {
    render()
    const mo = new MutationObserver(render)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-route'] })
    window.addEventListener('popstate', render)
    window.addEventListener('hashchange', render)
    window.addEventListener('pb-pages-mounted', render)
    return () => {
      mo.disconnect()
      window.removeEventListener('popstate', render)
      window.removeEventListener('hashchange', render)
      window.removeEventListener('pb-pages-mounted', render)
    }
  }, [])
  return null
}
